import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  FunnelIcon,
  ArrowPathIcon,
  TruckIcon,
  CalendarIcon,
  MapPinIcon,
  EyeIcon,
  ArrowLeftOnRectangleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const Consignments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get('order_id');
  const { user } = useAuth();
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showAgentSelectionModal, setShowAgentSelectionModal] = useState(false);
  const [salesAgents, setSalesAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [fromDate, setFromDate] = useState(subDays(new Date(), 30));
  const [toDate, setToDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [orderFilters, setOrderFilters] = useState({
    status: '',
    from_date: '',
    to_date: ''
  });
  const [orderPagination, setOrderPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });
  const [selectedOrderForConsignment, setSelectedOrderForConsignment] = useState(null);
  const [selectedAgentForConsignment, setSelectedAgentForConsignment] = useState('');
  const [insufficientStock, setInsufficientStock] = useState(false);
  const [stockValidation, setStockValidation] = useState({
    hasEnough: true,
    message: '',
    productName: '',
    available: 0,
    requested: 0
  });
  
  const [formData, setFormData] = useState({
    sales_agent_id: '',
    destination_town: '',
    items: [],
    expected_return_date: '',
    notes: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  // Check if user can create consignments
  const canCreateConsignment = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  // Check if user can view orders (only managers/owners)
  const canViewOrders = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  // Handle order_id from URL parameter (only for managers/owners)
  useEffect(() => {
    if (orderIdFromUrl && canCreateConsignment()) {
      handleOpenOrdersModalWithOrder(orderIdFromUrl);
    } else if (orderIdFromUrl && !canCreateConsignment()) {
      toast.error('Only managers and owners can create consignments from orders');
      navigate('/consignments');
    }
  }, [orderIdFromUrl]);

  useEffect(() => {
    fetchConsignments();
    if (canCreateConsignment()) {
      fetchSalesAgents();
      fetchProducts();
    }
  }, [pagination.page, pagination.per_page, selectedAgent, selectedStatus, fromDate, toDate, searchTerm]);

  useEffect(() => {
    validateStock();
  }, [formData.items]);

 const fetchConsignments = async () => {
  try {
    setLoading(true);
    const params = {
      page: pagination.page,
      per_page: pagination.per_page,
      status: selectedStatus || undefined,
      sales_agent_id: selectedAgent || undefined,
      from_date: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
      to_date: toDate ? format(toDate, 'yyyy-MM-dd') : undefined,
      search: searchTerm || undefined
    };

    Object.keys(params).forEach(key => 
      params[key] === undefined && delete params[key]
    );

    const response = await api.get('/consignments/', { params });
    setConsignments(response.data.items || []);
    setPagination(prev => ({
      ...prev,
      total: response.data.total
    }));
  } catch (error) {
    console.error('Error fetching consignments:', error);
    
    // Check if it's an authentication error
    if (error.response?.status === 401) {
      // Clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      toast.error('Session expired. Please login again.');
      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 2000);
    } else if (error.response?.status === 500) {
      toast.error('Server error. Please try again later.');
    } else {
      toast.error('Failed to fetch consignments');
    }
  } finally {
    setLoading(false);
  }
};

const fetchSalesAgents = async () => {
  try {
    // Use the dedicated sales agents endpoint
    const response = await api.get('/users/sales-agents', {
      params: { is_active: true, per_page: 1000 }
    });
    console.log("Sales agents from dedicated endpoint:", response.data);
    setSalesAgents(response.data.items || []);
  } catch (error) {
    console.error('Failed to fetch sales agents:', error);
    // Fallback to the old method if needed
    try {
      const fallbackResponse = await api.get('/users/', {
        params: { user_type: 'sales_agent', per_page: 1000 }
      });
      const agents = (fallbackResponse.data.items || []).filter(agent => 
        agent.user_type === 'sales_agent' && agent.profile?.id
      );
      setSalesAgents(agents);
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
    }
  }
};

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/', {
        params: { per_page: 1000 }
      });
      setProducts(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchOrdersForAgent = async () => {
    if (!canViewOrders()) return;
    
    try {
      setOrdersLoading(true);
      const params = {
        page: orderPagination.page,
        per_page: orderPagination.per_page,
        status: 'pending',
        from_date: orderFilters.from_date || undefined,
        to_date: orderFilters.to_date || undefined,
        exclude_consignment: true
      };
      
      const response = await api.get('/orders/', { params });
      setOrders(response.data.items || []);
      setOrderPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOpenOrdersModal = () => {
    if (!canViewOrders()) {
      toast.error('Only managers and owners can view pending orders');
      return;
    }
    setOrderPagination({ page: 1, per_page: 10, total: 0 });
    fetchOrdersForAgent();
    setShowOrdersModal(true);
  };

  const handleOpenOrdersModalWithOrder = async (orderId) => {
    if (!canCreateConsignment()) {
      toast.error('Only managers and owners can create consignments from orders');
      return;
    }
    
    try {
      const response = await api.get(`/orders/${orderId}`);
      const order = response.data;
      
      const pendingItems = (order.items || []).filter(item => {
        const pendingCases = item.quantity_cases - (item.delivered_cases || 0);
        return pendingCases > 0;
      });
      
      if (pendingItems.length === 0) {
        toast.error('This order has no pending items to fulfill');
        return;
      }
      
      let destinationTown = order.shop_owner_town || '';
      
      setSelectedOrderForConsignment({
        ...order,
        _destination_town: destinationTown
      });
      
      setShowAgentSelectionModal(true);
      
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const validateStock = async () => {
    if (formData.items.length === 0) {
      setStockValidation({
        hasEnough: true,
        message: '',
        productName: '',
        available: 0,
        requested: 0
      });
      setInsufficientStock(false);
      return;
    }

    setStockValidation({
      hasEnough: true,
      message: '',
      productName: '',
      available: 0,
      requested: 0
    });
    setInsufficientStock(false);
  };

  const handleCreateConsignment = async () => {
    if (!canCreateConsignment()) {
      toast.error('Only managers and owners can create consignments');
      return;
    }
    
    try {
      const submissionData = {
        sales_agent_id: formData.sales_agent_id,
        destination_town: formData.destination_town,
        expected_return_date: formData.expected_return_date || null,
        notes: formData.notes || '',
        items: formData.items.map(item => ({
          product_id: item.product_id,
          quantity_cases: item.quantity_cases
        }))
      };

      const res = await api.post('/consignments/', submissionData);
      toast.success('Consignment created successfully');
      setShowModal(false);
      resetForm();
      fetchConsignments();
    } catch (error) {
      if (error.response?.data?.error?.includes('Insufficient stock')) {
        toast.error(error.response.data.error, {
          duration: 6000,
          icon: '⚠️'
        });
      } else {
        toast.error(error.response?.data?.error || 'Failed to create consignment');
      }
    }
  };

  const handleCreateConsignmentFromOrder = async (order) => {
    if (!canCreateConsignment()) {
      toast.error('Only managers and owners can create consignments from orders');
      return;
    }
    
    try {
      const pendingItems = (order.items || []).filter(item => {
        const pendingCases = item.quantity_cases - (item.delivered_cases || 0);
        return pendingCases > 0;
      });
      
      if (pendingItems.length === 0) {
        toast.error('This order has no pending items to fulfill - all items have been delivered', {
          duration: 5000,
          icon: '📦'
        });
        return;
      }
      
      const loadingToast = toast.loading(`Preparing consignment for order ${order.order_number}...`);
      
      let destinationTown = order.shop_owner_town || '';
      
      if (!destinationTown && order.shop_owner_id) {
        try {
          const shopOwnerResponse = await api.get(`/users/shop-owners/${order.shop_owner_id}`);
          destinationTown = shopOwnerResponse.data.town || '';
        } catch (err) {
          console.error('Failed to fetch shop owner details:', err);
        }
      }
      
      if (!destinationTown) {
        toast.dismiss(loadingToast);
        const town = window.prompt('Please enter the destination town for this consignment:');
        if (!town) {
          toast.error('Destination town is required');
          return;
        }
        destinationTown = town;
      }
      
      toast.dismiss(loadingToast);
      
      setSelectedOrderForConsignment({
        ...order,
        _destination_town: destinationTown
      });
      setShowAgentSelectionModal(true);
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to prepare consignment');
    }
  };

  const handleConfirmAgentSelection = async () => {
    if (!canCreateConsignment()) {
      toast.error('Only managers and owners can create consignments');
      return;
    }
    
    if (!selectedAgentForConsignment) {
      toast.error('Please select a sales agent');
      return;
    }
    
    try {
      const orderCheck = await api.get(`/orders/${selectedOrderForConsignment.id}`);
      if (orderCheck.data.has_consignment) {
        toast.error('This order has already been assigned to a consignment');
        setShowAgentSelectionModal(false);
        setSelectedOrderForConsignment(null);
        setSelectedAgentForConsignment('');
        fetchOrdersForAgent();
        return;
      }
      
      const pendingItems = (selectedOrderForConsignment.items || []).filter(item => {
        const pendingCases = item.quantity_cases - (item.delivered_cases || 0);
        return pendingCases > 0;
      });
      
      let destinationTown = selectedOrderForConsignment._destination_town || 
                            selectedOrderForConsignment.shop_owner_town || '';
      
      if (!destinationTown) {
        destinationTown = window.prompt('Please enter the destination town for this consignment:');
        if (!destinationTown) {
          toast.error('Destination town is required');
          return;
        }
      }
      
      const loadingToast = toast.loading('Creating consignment...');
      
      const consignmentData = {
        sales_agent_id: selectedAgentForConsignment,
        destination_town: destinationTown,
        items: pendingItems.map(item => ({
          product_id: item.product_id,
          quantity_cases: item.quantity_cases - (item.delivered_cases || 0)
        })),
        expected_return_date: selectedOrderForConsignment.delivery_date || '',
        notes: `Created from order ${selectedOrderForConsignment.order_number}. Original order date: ${selectedOrderForConsignment.order_date}`
      };
      
      consignmentData.items = consignmentData.items.filter(item => item.quantity_cases > 0);
      
      if (consignmentData.items.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('This order has no pending items to fulfill');
        setShowAgentSelectionModal(false);
        setSelectedOrderForConsignment(null);
        setSelectedAgentForConsignment('');
        fetchOrdersForAgent();
        return;
      }
      
      console.log('Creating consignment with data:', consignmentData);
      
      const response = await api.post('/consignments/', consignmentData);
      
      await api.put(`/orders/${selectedOrderForConsignment.id}/link-consignment`, {
        consignment_id: response.data.consignment.id
      });
      
      toast.dismiss(loadingToast);
      toast.success(`Consignment ${response.data.consignment.consignment_number} created successfully!`);
      
      setShowAgentSelectionModal(false);
      setSelectedOrderForConsignment(null);
      setSelectedAgentForConsignment('');
      
      fetchOrdersForAgent();
      fetchConsignments();
      
      navigate('/consignments');
      
    } catch (error) {
      toast.dismiss();
      console.error('Error creating consignment:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to create consignment');
    }
  };

  const ordersColumns = [
    {
      header: 'Order #',
      accessorKey: 'order_number',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">{getValue()}</span>
      )
    },
    {
      header: 'Date',
      accessorKey: 'order_date',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : '-'
    },
    {
      header: 'Customer',
      accessorKey: 'shop_owner_name',
      cell: ({ getValue }) => (
        <div className="text-sm font-medium">{getValue()}</div>
      )
    },
    {
      header: 'Items',
      accessorKey: 'items_count',
      cell: ({ getValue }) => `${getValue()} items`
    },
    {
      header: 'Delivery Progress',
      cell: ({ row }) => {
        const order = row.original;
        const items = order.items || [];
        const totalItems = items.reduce((sum, item) => sum + item.quantity_cases, 0);
        const deliveredItems = items.reduce((sum, item) => sum + (item.delivered_cases || 0), 0);
        const percentage = totalItems > 0 ? (deliveredItems / totalItems) * 100 : 0;
        
        return (
          <div className="w-full">
            <div className="flex justify-between text-xs mb-1">
              <span>{deliveredItems} / {totalItems} cases</span>
              <span>{percentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${percentage >= 100 ? 'bg-green-500' : 'bg-primary-500'}`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>
          </div>
        );
      }
    },
    {
      header: 'Total',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />
    },
    {
      header: 'Action',
      cell: ({ row }) => {
        // Only show action for managers and owners
        if (!canCreateConsignment()) return null;
        
        const order = row.original;
        const items = order.items || [];
        const totalItems = items.reduce((sum, item) => sum + item.quantity_cases, 0);
        const deliveredItems = items.reduce((sum, item) => sum + (item.delivered_cases || 0), 0);
        const hasPendingItems = totalItems > deliveredItems;
        const hasConsignment = order.has_consignment === true;
        
        if (hasConsignment) {
          return (
            <span className="text-green-600 text-sm font-medium inline-flex items-center">
              <CheckIcon className="h-4 w-4 mr-1" />
              Assigned
            </span>
          );
        }
        
        return (
          <button
            onClick={() => handleCreateConsignmentFromOrder(order)}
            className={`text-sm font-medium inline-flex items-center px-3 py-1 rounded-md ${
              hasPendingItems && order.status !== 'completed' && order.status !== 'cancelled'
                ? 'text-primary-600 hover:text-primary-800 hover:bg-primary-50'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            disabled={!hasPendingItems || order.status === 'completed' || order.status === 'cancelled'}
            title={!hasPendingItems ? 'All items have been delivered' : 'Create consignment for pending items'}
          >
            <TruckIcon className="h-4 w-4 mr-1" />
            Create Consignment
          </button>
        );
      }
    }
  ];

  const resetForm = () => {
    setFormData({
      sales_agent_id: '',
      destination_town: '',
      items: [],
      expected_return_date: '',
      notes: ''
    });
    setInsufficientStock(false);
    setStockValidation({
      hasEnough: true,
      message: '',
      productName: '',
      available: 0,
      requested: 0
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: '',
        quantity_cases: 1
      }]
    });
  };

  const handleProductChange = (index, productId) => {
    const newItems = [...formData.items];
    newItems[index].product_id = productId;
    setFormData({...formData, items: newItems});
  };

  const handleQuantityChange = (index, quantity) => {
    const newItems = [...formData.items];
    newItems[index].quantity_cases = parseInt(quantity) || 0;
    setFormData({...formData, items: newItems});
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({...formData, items: newItems});
  };

  const getSelectedProduct = (productId) => {
    return products.find(p => p.id === productId);
  };

  const clearFilters = () => {
    setSelectedAgent('');
    setSelectedStatus('');
    setFromDate(subDays(new Date(), 30));
    setToDate(new Date());
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewDetails = (consignmentId) => {
    navigate(`/consignments/${consignmentId}`);
  };

  const columns = [
    {
      header: 'Consignment Details',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-primary-600">{row.original.consignment_number}</div>
          <div className="text-xs text-gray-500 mt-1">
            <span className="inline-flex items-center">
              <TruckIcon className="h-3 w-3 mr-1" />
              {row.original.items_count || 0} products
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Depot',
      accessorKey: 'depot_name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.depot_name || 'N/A'}</div>
          <div className="text-xs text-gray-500 flex items-center">
            <MapPinIcon className="h-3 w-3 mr-1" />
            {row.original.depot_location || 'N/A'}
          </div>
        </div>
      )
    },
    {
      header: 'Sales Agent',
      accessorKey: 'sales_agent_name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.sales_agent_name || 'N/A'}</div>
          <div className="text-xs text-gray-500">→ {row.original.destination_town || 'N/A'}</div>
        </div>
      )
    },
    {
      header: 'Case Movement',
      cell: ({ row }) => {
        const issued = row.original.total_cases_issued || 0;
        const sold = row.original.total_cases_sold || 0;
        const returned = row.original.total_cases_returned || 0;
        const remaining = issued - sold - returned;
        
        return (
          <div className="space-y-1 min-w-[120px]">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Issued:</span>
              <span className="font-medium">{issued}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-green-600">Sold:</span>
              <span className="font-medium text-green-600">{sold}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-blue-600">Returned:</span>
              <span className="font-medium text-blue-600">{returned}</span>
            </div>
            <div className="flex justify-between text-xs border-t pt-1 mt-1">
              <span className="text-gray-500">Remaining:</span>
              <span className={`font-medium ${remaining > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                {remaining}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      header: 'Dates',
      cell: ({ row }) => (
        <div className="space-y-1 min-w-[130px]">
          <div className="flex items-center text-xs">
            <CalendarIcon className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
            <span className="text-gray-600 truncate">Issued: {row.original.issued_date ? format(new Date(row.original.issued_date), 'dd MMM yy') : 'N/A'}</span>
          </div>
          {row.original.expected_return_date && (
            <div className="flex items-center text-xs">
              <CalendarIcon className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
              <span className="text-gray-600 truncate">Expected Returning: {format(new Date(row.original.expected_return_date), 'dd MMM yy')}</span>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue() || 'unknown'} />
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetails(row.original.id)}
            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {/* Sales agents can process returns on their assigned consignments */}
          {row.original.status === 'issued' && user?.user_type === 'sales_agent' && (
            <button
              onClick={() => handleViewDetails(row.original.id)}
              className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors"
              title="Process Return"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Consignments</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage stock consignments to sales agents.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          {/* Only show Pending Orders button for managers and owners */}
          {canViewOrders() && (
            <button
              type="button"
              onClick={handleOpenOrdersModal}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
              Pending Orders
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {/* Only show New Consignment button for managers and owners */}
          {canCreateConsignment() && (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Consignment
            </button>
          )}
        </div>
      </div>

      {/* Filters - Only show sales agent filter for managers/owners */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Consignments</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                placeholder="Search by consignment # or destination..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Only show Sales Agent filter for managers/owners */}
            {canCreateConsignment() && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Sales Agent</label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                >
                  <option value="">All Agents</option>
                  {salesAgents.map(agent => (
                    <option key={agent.id} value={agent.profile?.id || agent.id}>
                      {agent.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="issued">Issued</option>
                <option value="completed">Completed</option>
                <option value="partial">Partial</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchConsignments}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={date => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <DatePicker
                selected={toDate}
                onChange={date => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                maxDate={new Date()}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>
        </div>
      )}

      {/* Consignments Table */}
      <div className="mt-8">
        <DataTable
          data={consignments}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create Consignment Modal - Only shown to managers/owners */}
      {canCreateConsignment() && (
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          title="Create New Consignment"
          size="lg"
        >
          <form onSubmit={(e) => {
            e.preventDefault();
            handleCreateConsignment();
          }}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Sales Agent *</label>
                <select
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.sales_agent_id}
                  onChange={(e) => setFormData({...formData, sales_agent_id: e.target.value})}
                >
                  <option value="">Select Sales Agent</option>
                  {salesAgents.map(agent => (
                    <option key={agent.id} value={agent.profile?.id || agent.id}>
                      {agent.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Destination Town *</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.destination_town}
                  onChange={(e) => setFormData({...formData, destination_town: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Expected Return Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.expected_return_date}
                  onChange={(e) => setFormData({...formData, expected_return_date: e.target.value})}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items *</label>
                
                {insufficientStock && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">Insufficient Stock</p>
                      <p>{stockValidation.message}</p>
                    </div>
                  </div>
                )}

                {formData.items.map((item, index) => {
                  const selectedProduct = getSelectedProduct(item.product_id);
                  
                  return (
                    <div key={index} className="flex items-center gap-3 mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-[4]">
                        <select
                          required
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={item.product_id || ''}
                          onChange={(e) => handleProductChange(index, e.target.value)}
                        >
                          <option value="">Select Product</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.unit_volume_ml} ml) ({product.bottles_per_case} bottles/case)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex-[1]">
                        <input
                          type="number"
                          min="1"
                          required
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-center focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="Cases"
                          value={item.quantity_cases}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="flex items-center justify-center w-9 h-9 text-red-600 hover:text-red-800 font-bold text-xl rounded-md hover:bg-red-50"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-800 font-medium inline-flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Item
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  disabled={formData.items.length === 0}
                >
                  Create Consignment
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Orders Modal - Only shown to managers/owners */}
      {canViewOrders() && (
        <Modal
          isOpen={showOrdersModal}
          onClose={() => {
            setShowOrdersModal(false);
            setOrderFilters({ status: '', from_date: '', to_date: '' });
            setOrderPagination({ page: 1, per_page: 10, total: 0 });
          }}
          title="Pending Orders"
          size="xl"
        >
          <div className="space-y-4">
            {/* Order Filters */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={orderFilters.status}
                  onChange={(e) => {
                    setOrderFilters({...orderFilters, status: e.target.value});
                    setOrderPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">From Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={orderFilters.from_date}
                  onChange={(e) => {
                    setOrderFilters({...orderFilters, from_date: e.target.value});
                    setOrderPagination(prev => ({ ...prev, page: 1 }));
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">To Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={orderFilters.to_date}
                  onChange={(e) => {
                    setOrderFilters({...orderFilters, to_date: e.target.value});
                    setOrderPagination(prev => ({ ...prev, page: 1 }));
                  }}
                />
              </div>
            </div>
            
            <button
              onClick={() => fetchOrdersForAgent()}
              className="mb-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Apply Filters
            </button>

            {/* Orders Table */}
            <DataTable
              data={orders}
              columns={ordersColumns}
              loading={ordersLoading}
              pageSize={orderPagination.per_page}
              total={orderPagination.total}
              page={orderPagination.page}
              onPageChange={(page) => setOrderPagination(prev => ({ ...prev, page }))}
              onPageSizeChange={(per_page) => setOrderPagination(prev => ({ ...prev, per_page, page: 1 }))}
            />
          </div>
        </Modal>
      )}

      {/* Agent Selection Modal - Only shown to managers/owners */}
      {canCreateConsignment() && (
        <Modal
          isOpen={showAgentSelectionModal}
          onClose={() => {
            setShowAgentSelectionModal(false);
            setSelectedOrderForConsignment(null);
            setSelectedAgentForConsignment('');
          }}
          title="Assign Sales Agent"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a sales agent to fulfill order <span className="font-medium">{selectedOrderForConsignment?.order_number}</span>
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination Town *
              </label>
              <input
                type="text"
                required
                className="form-input"
                value={selectedOrderForConsignment?._destination_town || selectedOrderForConsignment?.shop_owner_town || ''}
                onChange={(e) => {
                  setSelectedOrderForConsignment(prev => ({
                    ...prev,
                    _destination_town: e.target.value
                  }));
                }}
                placeholder="Enter destination town"
              />
              <p className="mt-1 text-xs text-gray-500">
                The town where the goods will be delivered
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sales Agent *
              </label>
              <select
                required
                className="form-input"
                value={selectedAgentForConsignment}
                onChange={(e) => setSelectedAgentForConsignment(e.target.value)}
              >
                <option value="">Select a sales agent</option>
                {salesAgents.map(agent => (
                  <option key={agent.sales_agent_id} value={agent.sales_agent_id}>
                    {agent.full_name} {agent.employee_id ? `(${agent.employee_id})` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Show order items summary */}
            {selectedOrderForConsignment && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Order Items Summary</p>
                {(selectedOrderForConsignment.items || []).map((item, idx) => {
                  const pendingCases = item.quantity_cases - (item.delivered_cases || 0);
                  if (pendingCases <= 0) return null;
                  return (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.product_name || `Product ${item.product_id}`}</span>
                      <span className="font-medium">{pendingCases} cases</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAgentSelectionModal(false);
                  setSelectedOrderForConsignment(null);
                  setSelectedAgentForConsignment('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAgentSelection}
                className="btn-primary"
                disabled={!selectedAgentForConsignment || !(selectedOrderForConsignment?._destination_town || selectedOrderForConsignment?.shop_owner_town)}
              >
                Create Consignment
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Consignments;